import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, FormControlLabel, IconButton,
  InputLabel, Menu, MenuItem, Paper, Select, Snackbar, Stack, Switch, Tab, Tabs, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import api from '../services/api';

type Decision = 'saved' | 'disliked' | 'listed' | null;
type View = 'feed' | 'seen' | 'saved' | 'hidden';
interface FeedItem {
  key: string; shop: string; cat: string | null; name: string | null; url: string;
  img: string | null; price_krw: number | null; is_new: boolean; decision: Decision;
  near_sku: string | null;
}
interface FeedResponse {
  day: string | null; seen_cutoff: string; total: number; items: FeedItem[];
  cats: Record<string, number>; shops: { shop: string; n: number }[];
  counts: Record<View, number>;
}
interface Undo { key: string; previous: Decision; current: Decision; message: string }
const CATS: [string, string][] = [
  ['shirt', '襯衫'], ['pants', '褲子'], ['top', '上衣'], ['knit', '針織'],
  ['outer', '外套'], ['acc', '配件'], ['shoes', '鞋子'],
];
const PAGE = 120;            // the server's max; ~16 cards fill a phone screen now
const LONG_PRESS_MS = 450;
const SEEN_FLUSH_MS = 4000;
const belongs = (view: View, decision: Decision) => view === 'saved' ? decision === 'saved'
  : view === 'hidden' ? decision === 'disliked' || decision === 'listed'
    : view === 'seen' ? decision === null
      : decision !== 'disliked' && decision !== 'listed';

const ScoutFeedPage: React.FC = () => {
  const [view, setView] = useState<View>('feed');
  const [cat, setCat] = useState('');
  const [shop, setShop] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);
  const [sort, setSort] = useState<'score' | 'new'>('score');
  const [data, setData] = useState<FeedResponse | null>(null);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; item: FeedItem } | null>(null);
  const reqId = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const query = JSON.stringify([view, cat, shop, onlyNew, sort]);
  const queryRef = useRef(query);
  queryRef.current = query;
  const viewRef = useRef(view);
  viewRef.current = view;
  // One visit = one cutoff: items seen before the page opened are hidden; items seen now stay put.
  const cutoffRef = useRef<string | undefined>(undefined);
  const [seenThisVisit, setSeenThisVisit] = useState(0);
  const seenQueue = useRef<Set<string>>(new Set());
  const seenSent = useRef<Set<string>>(new Set());

  const flushSeen = useCallback(async () => {
    const keys = Array.from(seenQueue.current).slice(0, 500);
    if (!keys.length) return;
    keys.forEach((key) => { seenQueue.current.delete(key); seenSent.current.add(key); });
    try {
      await api.post('/api/scout-feed/seen', { keys });
    } catch {
      // Retry on the next flush; a lost batch only means those cards show once more.
      keys.forEach((key) => { seenSent.current.delete(key); seenQueue.current.add(key); });
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(flushSeen, SEEN_FLUSH_MS);
    const onHide = () => { if (document.visibilityState === 'hidden') flushSeen(); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
      flushSeen();
    };
  }, [flushSeen]);

  // A card counts as seen once it scrolls up past the top of the screen.
  const observer = useRef<IntersectionObserver | null>(null);
  if (!observer.current && typeof IntersectionObserver !== 'undefined') {
    observer.current = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const key = (entry.target as HTMLElement).dataset.seenKey;
        if (!key || entry.isIntersecting || !entry.target.isConnected || !entry.rootBounds) continue;
        // Removed or never-laid-out cards report a zero box; only a real scroll-past counts.
        if (entry.boundingClientRect.height === 0) continue;
        if (entry.boundingClientRect.bottom > entry.rootBounds.top) continue;
        if (seenSent.current.has(key) || seenQueue.current.has(key)) continue;
        seenQueue.current.add(key);
        setSeenThisVisit((n) => n + 1);
      }
    });
  }
  useEffect(() => () => observer.current?.disconnect(), []);
  const tracked = useRef<Map<string, HTMLElement>>(new Map());
  const trackSeen = (key: string) => (node: HTMLElement | null) => {
    const previous = tracked.current.get(key);
    if (previous && previous !== node) { observer.current?.unobserve(previous); tracked.current.delete(key); }
    if (node && previous !== node) { observer.current?.observe(node); tracked.current.set(key, node); }
  };

  const load = useCallback(async (offset: number, count = PAGE) => {
    const id = ++reqId.current;
    const isCurrent = () => id === reqId.current && query === queryRef.current;
    setLoading(true);
    setError('');
    try {
      // Refresh in bounded pages after removals, keeping the next offset valid.
      let collected: FeedItem[] = [];
      let response: FeedResponse;
      do {
        const res = await api.get<FeedResponse>('/api/scout-feed', {
          params: { view, cat: cat || undefined, shop: shop || undefined,
            new: onlyNew ? '1' : undefined, sort, offset: offset + collected.length,
            limit: Math.min(PAGE, count - collected.length), seen_cutoff: cutoffRef.current },
        });
        if (!isCurrent()) return;
        response = res.data;
        if (!cutoffRef.current) cutoffRef.current = response.seen_cutoff;
        collected = [...collected, ...response.items];
        if (response.items.length === 0) break;
      } while (collected.length < count && offset + collected.length < response.total);
      setData(response!);
      setItems((previous) => offset === 0 ? collected : [...previous, ...collected]);
    } catch (e: any) {
      if (isCurrent()) setError(e?.response?.data?.message || '載入失敗，請重試');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [view, cat, shop, onlyNew, sort, query]);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    setItems([]);
    setData(null);
    setMenu(null);
    load(0);
    return () => { ++reqId.current; };
  }, [load]);

  // Optimistic: the card changes on tap and the next tap never waits for the server (going through
  // ~200 new products a day is tap-tap-tap). No reload after a decision: a card that leaves this view
  // has left it server-side too, so the next page's offset (items.length) stays aligned.
  const inFlight = useRef<Set<string>>(new Set());
  const decide = async (key: string, status: Decision, previous: Decision) => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setPending(key);
    setMenu(null);
    setActionError('');
    const currentView = viewRef.current;
    const apply = (to: Decision, from: Decision) => {
      setItems((current) => current.map((item) => item.key === key ? { ...item, decision: to } : item)
        .filter((item) => belongs(currentView, item.decision)));
      setData((current) => current ? { ...current,
        total: Math.max(0, current.total + Number(belongs(currentView, to)) - Number(belongs(currentView, from))),
        counts: { ...current.counts, ...Object.fromEntries((['feed', 'saved', 'hidden'] as View[]).map((bucket) =>
          [bucket, Math.max(0, current.counts[bucket] + Number(belongs(bucket, to)) - Number(belongs(bucket, from)))])) },
      } : current);
    };
    // Undo can bring back a card that already left this list; only a reload can put it in place.
    const returning = belongs(currentView, status) && !itemsRef.current.some((item) => item.key === key);
    apply(status, previous);
    try {
      await api.post('/api/scout-feed/decision', { key, status });
      if (returning) await loadRef.current(0, Math.max(PAGE, itemsRef.current.length + 1));
      setUndo({ key, previous, current: status, message: status === 'saved' ? '已加入想要清單'
        : status === 'disliked' ? '已隱藏：不喜歡' : status === 'listed' ? '已隱藏：已上架'
          : previous === 'saved' ? '已取消想要' : '已恢復顯示' });
    } catch (e: any) {
      setActionError(e?.response?.data?.message || '變更未儲存，請再試一次');
      // A removed card can't be put back in place locally; reload what was on screen.
      if (belongs(currentView, previous) && !belongs(currentView, status)) {
        await loadRef.current(0, Math.max(PAGE, itemsRef.current.length + 1));
      } else apply(previous, status);
    } finally {
      inFlight.current.delete(key);
      setPending((current) => current === key ? null : current);
    }
  };

  // Long-press (touch) or right-click opens the card menu: name, price, the Korean page, 已上架.
  const press = useRef<{ timer?: number; fired: boolean }>({ fired: false });
  const pressHandlers = (item: FeedItem) => ({
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      const anchor = event.currentTarget;
      press.current.fired = false;
      window.clearTimeout(press.current.timer);
      press.current.timer = window.setTimeout(() => {
        press.current.fired = true;
        setMenu({ anchor, item });
      }, LONG_PRESS_MS);
    },
    onPointerUp: () => window.clearTimeout(press.current.timer),
    onPointerLeave: () => window.clearTimeout(press.current.timer),
    onPointerCancel: () => window.clearTimeout(press.current.timer),
    onContextMenu: (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      window.clearTimeout(press.current.timer);
      press.current.fired = true;
      setMenu({ anchor: event.currentTarget, item });
    },
  });
  const tapImage = (item: FeedItem) => {
    if (press.current.fired) { press.current.fired = false; return; }
    // The 想要 list is where listing starts, so a tap there opens the Korean page (Magnus 10-28);
    // 取消想要 moves to the long-press menu.
    if (view === 'saved') { if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer'); return; }
    decide(item.key, item.decision === 'saved' ? null : 'saved', item.decision);
  };

  // Load the next page before the bottom is reached, so scrolling never stops at a button.
  const sentinel = useRef<HTMLDivElement | null>(null);
  const canLoadMore = Boolean(data && items.length < data.total && !loading && !error);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !canLoadMore || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadRef.current(itemsRef.current.length);
    }, { rootMargin: '1200px 0px' });
    io.observe(node);
    return () => io.disconnect();
  }, [canLoadMore, items.length]);

  const allCount = data ? Object.values(data.cats).reduce((a, b) => a + b, 0) : 0;
  const filtered = Boolean(cat || shop || onlyNew);
  const resetFilters = () => { setCat(''); setShop(''); setOnlyNew(false); };
  const emptyText = filtered ? '沒有符合篩選條件的商品'
    : view === 'saved' ? '還沒有想要的商品，先從探索新款挑選想上架的款式'
      : view === 'hidden' ? '還沒有隱藏商品'
        : view === 'seen' ? '還沒有看過的商品，之前滑過的款式會出現在這裡'
          : !data?.day ? '還沒有資料，等今天的雷達跑完'
            : '今天的新款都看完了，明天雷達會再更新';

  return (
    // maxWidth: the app shell's <main> (24px padding, min-width auto) grows to its widest child, so on a
    // phone the sideways chip row would widen the whole page past the screen. mx -2 takes back most of that
    // padding so the 4-up pictures are big enough to judge.
    <Box sx={{ p: { xs: 1, sm: 3 }, mx: { xs: -2, sm: 0 }, maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' }, boxSizing: 'border-box' }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 0.5 }}>
        <Typography variant="h5" fontWeight={500}>新款雷達</Typography>
        {data?.day && <Typography variant="body2" color="text.secondary">
          {String(data.day).slice(0, 10)} 更新
        </Typography>}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: 12, sm: 14 } }}>
        最新上架的排最前面。點圖片＝想要，左下 ✓＝已上架，右下 ✕＝不喜歡，長按（電腦按右鍵）看品名、價格、開韓國網站。看過的和想要的，下次打開就不再出現。想要清單裡點圖片＝開韓國網站去上架。
      </Typography>
      <Tabs value={view} onChange={(_, next: View) => setView(next)} variant="scrollable" allowScrollButtonsMobile
        aria-label="新款雷達清單" sx={{ mb: 1, minHeight: { xs: 40, sm: 48 }, '& .MuiTab-root': { minHeight: { xs: 40, sm: 48 }, py: { xs: 0.5, sm: 1.5 } } }}>
        <Tab value="feed" label={`還沒看過 ${data?.counts?.feed?.toLocaleString() ?? '—'}`} />
        <Tab value="saved" label={`想要清單 ${data?.counts?.saved?.toLocaleString() ?? '—'}`} />
        <Tab value="seen" label={`看過的 ${data?.counts?.seen?.toLocaleString() ?? '—'}`} />
        <Tab value="hidden" label={`已隱藏 ${data?.counts?.hidden?.toLocaleString() ?? '—'}`} />
      </Tabs>
      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mb: 2 }}>
        清單依你的帳號儲存。想要的款式會出現在每日看板的「待找貨源」，不會自動上架。
        {view === 'feed' && seenThisVisit > 0 && ` 這次已看過 ${seenThisVisit.toLocaleString()} 款。`}
      </Typography>
      {/* Phone: chips scroll sideways in one row so the grid starts higher. */}
      <Stack direction="row" spacing={1} sx={{ mb: { xs: 1, sm: 2 }, flexWrap: { xs: 'nowrap', sm: 'wrap' }, rowGap: 1,
        overflowX: { xs: 'auto', sm: 'visible' }, '& > *': { flexShrink: 0 } }}>
        <Chip label={`全部 ${allCount.toLocaleString()}`} color={cat === '' ? 'primary' : 'default'}
          variant={cat === '' ? 'filled' : 'outlined'} onClick={() => setCat('')} />
        {CATS.map(([key, label]) => <Chip key={key} label={`${label} ${(data?.cats[key] || 0).toLocaleString()}`}
          color={cat === key ? 'primary' : 'default'} variant={cat === key ? 'filled' : 'outlined'}
          onClick={() => setCat(key)} />)}
      </Stack>
      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" sx={{ mb: { xs: 1, sm: 2.5 }, flexWrap: 'wrap', rowGap: 1 }}>
        <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 200 } }}>
          <InputLabel id="scout-shop-label">店家</InputLabel>
          <Select labelId="scout-shop-label" label="店家" value={shop} onChange={(e) => setShop(e.target.value)}>
            <MenuItem value="">全部店家（{data?.shops.length || 0}）</MenuItem>
            {(data?.shops || []).map((entry) => <MenuItem key={entry.shop} value={entry.shop}>
              {entry.shop}（{entry.n}）
            </MenuItem>)}
            {shop && !data?.shops.some((entry) => entry.shop === shop) && <MenuItem value={shop}>{shop}</MenuItem>}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 140 } }}>
          <InputLabel id="scout-sort-label">排序</InputLabel>
          <Select labelId="scout-sort-label" label="排序" value={sort} onChange={(e) => setSort(e.target.value as 'score' | 'new')}>
            <MenuItem value="score">新到優先</MenuItem>
            <MenuItem value="new">最新出現</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Switch checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} />}
          label="只看新上架" />
        {data && <Typography variant="body2" color="text.secondary">{data.total.toLocaleString()} 款</Typography>}
      </Stack>
      {actionError && <Alert severity="error" onClose={() => setActionError('')} sx={{ mb: 2 }}>{actionError}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit"
        onClick={() => load(0, Math.max(PAGE, items.length))}>重試</Button>}>{error}</Alert>}
      {!loading && !error && data && items.length === 0 && <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyText}</Typography>
        {filtered && <Button onClick={resetFilters} sx={{ mt: 1 }}>清除篩選</Button>}
      </Paper>}
      <Box sx={{ display: 'grid', gap: { xs: 0.5, sm: 1 }, gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(180px, 1fr))', md: 'repeat(auto-fill, minmax(240px, 1fr))' } }}>
        {items.map((item) => {
          const label = item.name || item.shop;
          const saved = item.decision === 'saved';
          return <Box key={item.key} data-seen-key={item.key}
            ref={view === 'feed' && item.decision === null ? trackSeen(item.key) : undefined}
            sx={{ position: 'relative', aspectRatio: '3 / 4', bgcolor: 'grey.200', borderRadius: 0.5, overflow: 'hidden',
              outline: saved ? '3px solid' : 'none', outlineColor: 'primary.main', outlineOffset: -3 }}>
            <Box component="button" type="button" aria-pressed={view === 'saved' ? undefined : saved}
              aria-label={view === 'saved' ? `開啟韓國網站 ${label}` : `${saved ? '取消想要' : '想要'} ${label}`}
              title={`${label}${item.price_krw ? ` · ₩${item.price_krw.toLocaleString()}` : ''} · ${item.shop}`}
              disabled={view === 'hidden'} onClick={() => tapImage(item)} {...pressHandlers(item)}
              sx={{ all: 'unset', display: 'block', width: '100%', height: '100%', cursor: 'pointer',
                WebkitTouchCallout: 'none', userSelect: 'none', WebkitTapHighlightColor: 'transparent' }}>
              {item.img && <Box component="img" src={item.img} alt="" loading="lazy" referrerPolicy="no-referrer" draggable={false}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />}
            </Box>
            {item.is_new && <Box sx={{ position: 'absolute', top: 3, left: 3, bgcolor: 'primary.main', color: '#fff', pointerEvents: 'none',
              fontSize: 9, fontWeight: 500, px: 0.5, borderRadius: 0.5 }}>NEW</Box>}
            {item.near_sku && <Box title="照片很像我們在賣的這款：可能是同一件（換角度或換色），也可能只是相似"
              sx={{ position: 'absolute', top: item.is_new ? 20 : 3, left: 3, bgcolor: 'rgba(0,0,0,.72)', color: '#fff', pointerEvents: 'none',
                fontSize: 9, fontWeight: 500, px: 0.5, borderRadius: 0.5 }}>像 {item.near_sku}</Box>}
            {saved && <FavoriteIcon aria-hidden sx={{ position: 'absolute', top: 4, right: 4, fontSize: 18, color: 'primary.main',
              filter: 'drop-shadow(0 0 2px #fff)', pointerEvents: 'none' }} />}
            {view === 'hidden' ? <Box sx={{ position: 'absolute', inset: 'auto 0 0 0', bgcolor: 'rgba(255,255,255,.92)', p: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3 }}>
                隱藏原因：{item.decision === 'listed' ? '已上架' : '不喜歡'}
              </Typography>
              <Button size="small" fullWidth variant="outlined" disabled={pending === item.key}
                onClick={() => decide(item.key, null, item.decision)} sx={{ minWidth: 0, px: 0.5, fontSize: 11 }}>恢復顯示</Button>
            </Box> : <>
              {/* Both ways to hide a card are one tap (Magnus 10-28: "i need to take 2 steps to hide a product"),
                  on 想要 cards too: a wanted card gets marked 已上架 from the 想要 list once it's listed. */}
              <IconButton size="small" aria-label={`已上架 ${label}`} title="已上架" disabled={pending === item.key}
                onClick={() => decide(item.key, 'listed', item.decision)}
                sx={{ position: 'absolute', left: 2, bottom: 2, p: 0.5, bgcolor: 'rgba(255,255,255,.85)',
                  '&:hover': { bgcolor: '#fff' } }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
              <IconButton size="small" aria-label={`不喜歡 ${label}`} title="不喜歡" disabled={pending === item.key}
                onClick={() => decide(item.key, 'disliked', item.decision)}
                sx={{ position: 'absolute', right: 2, bottom: 2, p: 0.5, bgcolor: 'rgba(255,255,255,.85)',
                  '&:hover': { bgcolor: '#fff' } }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
            </>}
          </Box>;
        })}
      </Box>
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {menu && <Box sx={{ px: 2, py: 1, maxWidth: 260 }}>
          <Typography variant="body2">{menu.item.name || '（沒有品名）'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {menu.item.shop}{menu.item.price_krw ? ` · ₩${menu.item.price_krw.toLocaleString()}` : ''}
          </Typography>
        </Box>}
        <MenuItem component="a" href={menu?.item.url} target="_blank" rel="noopener noreferrer" onClick={() => setMenu(null)}>
          開啟韓國網站 ↗</MenuItem>
        {menu?.item.decision === 'saved' && <MenuItem disabled={pending === menu.item.key}
          onClick={() => decide(menu.item.key, null, 'saved')}>取消想要</MenuItem>}
        <MenuItem disabled={!menu || pending === menu.item.key} onClick={() => menu && decide(menu.item.key, 'listed', menu.item.decision)}>已上架</MenuItem>
        <MenuItem disabled={!menu || pending === menu.item.key} onClick={() => menu && decide(menu.item.key, 'disliked', menu.item.decision)}>不喜歡</MenuItem>
      </Menu>
      <Box ref={sentinel} sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        {loading ? <CircularProgress size={28} aria-label="載入商品" /> : data && items.length < data.total && <Button
          variant="outlined" disabled={Boolean(error)} onClick={() => load(items.length)}>
          載入更多（還有 {(data.total - items.length).toLocaleString()} 款）
        </Button>}
      </Box>
      <Snackbar open={Boolean(undo)} autoHideDuration={6000} onClose={(_, reason) => { if (reason !== 'clickaway') setUndo(null); }}
        message={undo?.message} action={<Button color="inherit" size="small" onClick={() => {
          if (!undo) return;
          decide(undo.key, undo.previous, undo.current);
          setUndo(null);
        }}>復原</Button>} />
    </Box>
  );
};

export default ScoutFeedPage;
